/* eslint-disable class-methods-use-this */
import { ContactTypeId } from '@constants/enums';
import { AccountTypes, IPayableClient } from '@efex-services/client/types';
import { IEnterpriseContact } from '@efex-services/payments/types';
import { type Country } from '../../../../configs/language';
import clientSvc from '../../../../services/client/clientSvc';
import { type IUploadDocuments } from '../../../../services/documents/types';
import { COUNTRY_CURRENCY } from '../../../common/Form/config';
import {
  NewPaymentMethods,
  PayloadData,
  TransactionTypes,
  type RawData,
} from '../types';

type DocumentsUpload = {
  documents:
    | { id: string; name: string; document_type_id: string }[]
    | undefined;
};

export default class FormatPaymentService {
  private determineCurrency = (
    payable?: RawData['payable'],
    client?: RawData['client']
  ) => {
    if (payable?.uuid && payable?.desCurrency) return payable.desCurrency;
    if (client?.currency) return client.currency;
    return COUNTRY_CURRENCY[client?.country as Country];
  };

  private uploadInvoiceFile = async (
    files: File[] | undefined
  ): Promise<DocumentsUpload> => {
    if (!files || !files.length) {
      return Promise.resolve({ documents: undefined });
    }
    try {
      const invoicePayload: IUploadDocuments = {
        documentTypeId: 1,
        totalDocuments: 1,
        documents: files as unknown as FormData,
      };
      return (await clientSvc.uploadDocuments(
        invoicePayload
      )) as unknown as DocumentsUpload;
    } catch {
      return Promise.resolve({ documents: undefined });
    }
  };

  private buildPayableClient = (
    rawContact: IEnterpriseContact,
    contactId: number
  ): IPayableClient => {
    const { naturalPerson, enterprise, bankAccount } = rawContact;
    return {
      country: enterprise?.country || naturalPerson?.country || 'USA',
      uuid: naturalPerson?.uuid || '',
      entityBusiness: enterprise?.entity_type as AccountTypes,
      name: enterprise.name,
      companyName: enterprise.name,
      origin: 'USER',
      currency: enterprise?.account_currency || 'USD',
      bankAccount: bankAccount || {},
      contactId: contactId || '',
    };
  };

  private getTransactionType = (
    contactType?: ContactTypeId,
    isForward?: boolean,
    activeRateUuid?: string | null,
    isPromoter?: boolean
  ): TransactionTypes => {
    if (activeRateUuid && isPromoter) {
      if (contactType === ContactTypeId.SWIFT_CONTACT) {
        return TransactionTypes.promoterSwift;
      }
      return TransactionTypes.promoter;
    }
    if (contactType === ContactTypeId.SWIFT_CONTACT)
      return TransactionTypes.swift;
    if (isForward) return TransactionTypes.payable;
    return TransactionTypes.payable;
  };

  build = async (
    rawData: RawData,
    isFeaturePayment = false
  ): Promise<PayloadData | undefined> => {
    if (!rawData) {
      return undefined;
    }
    const {
      amount,
      client,
      comments,
      currency,
      dueDate,
      invoices,
      isPendingTransfer,
      isSchedule,
      isForward,
      payable,
      paymentMethod,
      rateData,
      resultAmount,
      reverse,
      forwardValues,
      paymentReason,
      activeRateUuid,
      isPromoter,
    } = rawData;

    const { documents } = await this.uploadInvoiceFile(invoices);
    const invoiceIDs = documents?.map((document) => document.id);
    const destinyCurrency = this.determineCurrency(payable, client);
    const fixedReference =
      destinyCurrency !== currency ? rateData?.reference : '';

    if (isForward) {
      return {
        amount: Number(amount || 0),
        paymentReason,
        currency,
        contactId: null,
        desCurrency: destinyCurrency,
        invoice: invoiceIDs,
        paymentMethodId:
          NewPaymentMethods[paymentMethod as keyof typeof NewPaymentMethods],
        transactionTypeId: this.getTransactionType(
          undefined,
          isForward,
          activeRateUuid,
          isPromoter
        ),
        reference:
          (isPromoter ? activeRateUuid : null) ||
          forwardValues?.uuid ||
          fixedReference ||
          undefined,
        comment: comments,
        reverse,
        executionDate: dueDate?.toISODate() ?? undefined,
      } as PayloadData;
    }

    if (
      client?.contactTypeId === ContactTypeId.SWIFT_CONTACT ||
      isFeaturePayment
    ) {
      return {
        amount: Number(amount || 0),
        paymentReason,
        currency,
        contactId: client?.contactId || '',
        desCurrency: destinyCurrency,
        invoice: invoiceIDs,
        paymentMethodId:
          NewPaymentMethods[
            (paymentMethod as keyof typeof NewPaymentMethods) || 'balance'
          ],
        transactionTypeId: this.getTransactionType(
          client?.contactTypeId,
          isForward,
          activeRateUuid,
          isPromoter
        ),
        reference:
          (isPromoter ? activeRateUuid : null) ||
          forwardValues?.uuid ||
          fixedReference ||
          undefined,
        comment: comments,
        reverse,
        executionDate: dueDate?.toISODate() ?? undefined,
      } as PayloadData;
    }

    const formattedData: PayloadData = {
      uuid: payable?.uuid || '',
      client_uuid: client?.uuid || '',
      client_origin: client?.origin || '',
      amount: Number(amount || 0),
      desAmount: Number(resultAmount || 0),
      comment: comments,
      invoice: invoiceIDs,
      reference: (isPromoter ? activeRateUuid : null) || fixedReference || '',
      is_pending_transfer: isPendingTransfer,
      des_currency: destinyCurrency,
      currency,
      country_user_origin:
        rateData?.countryAccount || (client?.country as Country) || 'USA',
      country_origin: client?.country || '',
      reverse,
      execution_date: dueDate?.toISODate() ?? '',
      payment_schedule: isSchedule,
      paymentReason,
      transactionTypeId: this.getTransactionType(
        client?.contactTypeId,
        isForward,
        activeRateUuid,
        isPromoter
      ),
    };

    return formattedData;
  };
}
